/**
 * Utility functions for handling locale/language management
 * Priority: URL > localStorage > default
 */

const LOCALE_STORAGE_KEY = 'lnc-i18n';
export const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = ['en', 'es'] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const getLocaleFromStorage = (): SupportedLocale | null => {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
      return stored as SupportedLocale;
    }
  } catch (error) {
    console.warn('Error reading locale from localStorage:', error);
  }
  return null;
};

export const saveLocaleToStorage = (locale: SupportedLocale): void => {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch (error) {
    console.warn('Error saving locale to localStorage:', error);
  }
};

export const getLocaleFromUrl = (pathname: string): SupportedLocale | null => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    const firstSegment = segments[0];
    if (SUPPORTED_LOCALES.includes(firstSegment as SupportedLocale)) {
      return firstSegment as SupportedLocale;
    }
  }
  return null;
};

export const getInitialLocale = (pathname: string): SupportedLocale => {
  const urlLocale = getLocaleFromUrl(pathname);
  if (urlLocale) {
    const storedLocale = getLocaleFromStorage();
    if (storedLocale !== urlLocale) {
      saveLocaleToStorage(urlLocale);
    }
    return urlLocale;
  }
  const storedLocale = getLocaleFromStorage();
  if (storedLocale) {
    return storedLocale;
  }
  return DEFAULT_LOCALE;
};

export const isValidLocale = (locale: string): locale is SupportedLocale => {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
};
